// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';
import { RpaModuleNameSchema, type RpaModuleName } from './backend_config.types';
import { IpgSystemId, IpwSystemId } from './types/third-party-system.enums';
import type { RpaModuleAuthorizationTarget } from './rpa-module';

/**
 * Workflow delivery. Signed workflow statements are launcher contract v3
 * (samba-launcher-contract.ts); this module holds what PILLAR and the payload
 * exchange around them.
 */

export interface SambaWorkflowReleaseSequenceStateV1 {
    readonly schemaVersion: 1;
    readonly highestAcceptedReleaseSequences: Partial<Record<RpaModuleName, string>>;
}

const CanonicalDecimalStringSchema: z.ZodType<string> = z.string().regex(/^(?:0|[1-9]\d*)$/);

export const RpaModuleAuthorizationTargetSchema: z.ZodType<RpaModuleAuthorizationTarget> =
    z.discriminatedUnion('kind', [
        z
            .object({
                kind: z.literal('ipw'),
                systemId: z.union([
                    z.literal(IpwSystemId.GENERIC),
                    z.literal(IpwSystemId.DEALERTRACK),
                    z.literal(IpwSystemId.PCPARTPICKER),
                ]),
            })
            .strict(),
        z
            .object({
                kind: z.literal('ipg'),
                systemId: z.union([
                    z.literal(IpgSystemId.VALOR_US),
                    z.literal(IpgSystemId.CARDPOINTE),
                    z.literal(IpgSystemId.CLOVER),
                ]),
            })
            .strict(),
    ]);

/** Asks PILLAR for one workflow container for this platform (launcher contract v3). */
export interface SambaWorkflowReleaseQueryV2 {
    readonly schemaVersion: 2;
    readonly platform: string;
    readonly workflowProtocolVersion: number;
}

export const SambaWorkflowReleaseQueryV2Schema: z.ZodType<SambaWorkflowReleaseQueryV2> = z
    .object({
        schemaVersion: z.literal(2),
        platform: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/),
        workflowProtocolVersion: z.number().int().positive().safe(),
    })
    .strict();

export const SambaWorkflowReleaseQueryContract = {
    variants: { v2: { normalize: SambaWorkflowReleaseQueryV2Schema.parse } },
    writerVariant: 'v2',
} as const;

export const SambaWorkflowReleaseSequenceStateV1Schema: z.ZodType<SambaWorkflowReleaseSequenceStateV1> =
    z
        .object({
            schemaVersion: z.literal(1),
            highestAcceptedReleaseSequences: z.partialRecord(
                RpaModuleNameSchema,
                CanonicalDecimalStringSchema
            ),
        })
        .strict();

export const SambaWorkflowReleaseSequenceStateStorageContract = {
    variants: { v1: { normalize: SambaWorkflowReleaseSequenceStateV1Schema.parse } },
    writerVariant: 'v1',
} as const;
